import { inject, Injectable } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { environment } from '@environments/environment';
import { KeyService } from './key.service';

@Injectable({
  providedIn: 'root'
})
export class EncryptionService {

  private keyService = inject(KeyService);
  
  // ✅ Use environment-specific encryption key
  private readonly SECRET_KEY = this.keyService.getKey() || 'jpes-eclaims-default-secret-key-2026';

  /**
   * ✅ Encrypt any data
   */
  encrypt<T>(data: T): string {
    try {
      const jsonString = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(jsonString, this.SECRET_KEY).toString();
      return encrypted;
    } catch (error) {
      throw new Error('Encryption failed');
    }
  }

  /**
   * ✅ Decrypt data
   */
  decrypt<T>(encryptedData: string): T | null {
    try {
      const decrypted = CryptoJS.AES.decrypt(encryptedData, this.SECRET_KEY);
      const jsonString = decrypted.toString(CryptoJS.enc.Utf8);
      
      if (!jsonString) {
        return null;
      }
      
      return JSON.parse(jsonString) as T;
    } catch (error) {
      return null;
    }
  }

  /**
   * ✅ Check if string is encrypted (basic check)
   */
  isEncrypted(data: string): boolean {
    try {
      // Try to decrypt - if it fails, it's not encrypted
      const decrypted = CryptoJS.AES.decrypt(data, this.SECRET_KEY);
      const result = decrypted.toString(CryptoJS.enc.Utf8);
      return result.length > 0;
    } catch {
      return false;
    }
  }

  /**
   * ✅ Hash data (one-way encryption for passwords, etc.)
   */
  hash(data: string): string {
    return CryptoJS.SHA256(data).toString();
  }
}